/**
 * Split Builder Page
 * Design and configure workout splits with real-time volume tracking
 */

import { UnifiedButton } from '@/components/ui/unified-button'
import { ListPlus, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useUserCredentials } from '../api/hooks/useCredentials'
import {
  ExerciseSelectionModal,
  RoutineSelectionModal,
  SplitKanbanBoard,
  VolumeRoutineWidget
} from '../components/split-builder'
import { pushSplitToHevy } from '../services/hevyPushService'
import { useSplitConfigStore } from '../stores/splitConfigStore'

const SplitBuilderPage = () => {
  const { splitName, setSplitName, totalSets, routines, dayAssignments, cycleMode, cycleAssignments } = useSplitConfigStore()
  const { data: userCredentials } = useUserCredentials()
  const [isPushing, setIsPushing] = useState(false)

  const hasApiKey = !!(
    userCredentials?.apiKey &&
    userCredentials.apiKey !== 'CSV_IMPORT' &&
    userCredentials.apiKey.length > 10
  )
  const canPush = hasApiKey && totalSets > 0 && !isPushing

  const handlePushToHevy = async () => {
    if (!userCredentials?.apiKey || !canPush) return

    setIsPushing(true)

    try {
      const result = await pushSplitToHevy(userCredentials.apiKey, {
        splitName: splitName || 'My Split',
        routines,
        dayAssignments,
        cycleMode,
        cycleAssignments,
        createFolder: true,
        folderName: splitName || 'My Split'
      })

      if (result.success) {
        const uniqueCount = result.uniqueRoutinesCreated
        const dayCount = result.routines.filter((r) => r.routine).length
        toast.success(
          `Successfully pushed ${uniqueCount} routine${uniqueCount !== 1 ? 's' : ''} to Hevy!`,
          {
            description: result.folder
              ? `Created in folder: "${result.folder.title}" • Scheduled across ${dayCount} day${dayCount !== 1 ? 's' : ''}`
              : `Scheduled across ${dayCount} day${dayCount !== 1 ? 's' : ''}`
          }
        )
      } else {
        toast.error('Failed to push split to Hevy', {
          description: result.errors[0] || 'Unknown error occurred'
        })
      }
    } catch (error) {
      toast.error('Failed to push split to Hevy', {
        description: error instanceof Error ? error.message : 'Unknown error occurred'
      })
    } finally {
      setIsPushing(false)
    }
  }

  return (
    <div className="mx-auto max-w-screen-lg p-4 md:p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Split Builder</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Design your workout split and track muscle group volume
          </p>
        </div>

        {/* Volume Widget */}
        <VolumeRoutineWidget />

        {/* 7-Day Kanban Board */}
        <SplitKanbanBoard />

        {/* Split Name Input - Bottom */}
        <div className="space-y-2">
          <label htmlFor="split-name" className="text-sm font-medium">
            Split Name
          </label>
          <input
            id="split-name"
            type="text"
            value={splitName}
            onChange={(e) => setSplitName(e.target.value)}
            placeholder="e.g., Push Pull Legs"
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Push to Hevy Button */}
        <div className="flex justify-end">
          <UnifiedButton onClick={handlePushToHevy} disabled={!canPush}>
            <div className="flex items-center gap-2">
              {isPushing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ListPlus className="h-4 w-4" />
              )}
              {isPushing ? 'Adding to Hevy...' : 'Add to Hevy'}
            </div>
          </UnifiedButton>
        </div>

        {/* Exercise Selection Modal */}
        <ExerciseSelectionModal />

        {/* Routine Selection Modal */}
        <RoutineSelectionModal />
      </div>
    </div>
  )
}

export default SplitBuilderPage
